/**
 * The backend, as typed calls generated from the backend's OpenAPI schemas.
 *
 * Each wrapper drives the loading bar and reports a failure (to Sentry, and to
 * apiHealth, which the status pill reads), so a caller cannot forget either.
 * It throws on a non-2xx instead of handing back an error body that reads as
 * a successful response.
 */
import { apiClient, dataClient } from "./client";
import { reportError } from "../lib/Toast";
import { apiHealth, reachability } from "../stores";
import { markAbsent, nextHealth } from "../lib/apiHealth";
import { answered, finished, started, unanswered } from "../lib/reachability";
import { progress } from "../lib/progress";
import { isTransient } from "../lib/timedFetch";
import { noteTransientFailure } from "../lib/recovery";
import type { components as ApiSchemas } from "./generated/api";
import type { components as DataSchemas } from "./generated/data";
import type { CountryNetwork } from "./events";
import type { AlternativeProduct } from "../lib/observedProduct";

type Schemas = ApiSchemas["schemas"];

export type RadarFrame = Schemas["RadarFrame"];
export type RadarFrames = Schemas["RadarFrames"];
export type VectorOverlay = Schemas["VectorOverlay"];
export type PrecipitationTypes = Schemas["PrecipitationTypes"];
export type ClassTiles = Schemas["ClassTiles"];
export type LightningLayerMetadata = Schemas["LightningLayerMetadata"];
export type LightningCollection = Schemas["LightningCollection"];
export type LightningStats = Schemas["LightningStats"];
export type Strike = DataSchemas["schemas"]["Strike"];
export type Mesocyclone = DataSchemas["schemas"]["Mesocyclone"];
export type CellTrack = DataSchemas["schemas"]["TrackFeature"];
export type CellTrackProperties = DataSchemas["schemas"]["TrackProperties"];
export type CellPlacement = DataSchemas["schemas"]["CellPlacement"];
export type CellStep = DataSchemas["schemas"]["CellStep"];
export type CellForecastPoint = DataSchemas["schemas"]["ForecastPoint"];
export type CellCurrent = DataSchemas["schemas"]["CellCurrent"];
export type CellLayer = DataSchemas["schemas"]["CellLayer"];
export type CellVolume = DataSchemas["schemas"]["CellVolume"];
/** A box of sky. Its `feature`, the patch it was cut from, is how it finds its storm. */
export type RadarVolume = DataSchemas["schemas"]["RadarVolume"];
export type CurrentVolumes = DataSchemas["schemas"]["CurrentVolumes"];
export type SiteSweeps = DataSchemas["schemas"]["SiteSweeps"];
export type CurrentSweeps = DataSchemas["schemas"]["CurrentSweeps"];
/** A storm, as a GeoJSON feature at its current centroid; its path is a property. */
export type Storm = DataSchemas["schemas"]["StormFeature"];
export type StormProperties = DataSchemas["schemas"]["StormProperties"];
export type StormStatus = StormProperties["status"];
export type StormCollection = DataSchemas["schemas"]["StormCollection"];
/** One storm with its KONRAD3D tracks and its tracker tracks. */
export type StormDetail = DataSchemas["schemas"]["StormDetail"];
export type TrackerTrack = DataSchemas["schemas"]["TrackerTrack"];
export type TrackerFeature = DataSchemas["schemas"]["TrackerFeature"];

/** The subset of NanobarWrapper these calls need. */
export interface Progress {
  start(id: string): void;
  finish(id: string): void;
}

/**
 * No bar, for a refresh nobody is waiting on: the volumes re-read as each
 * network's runs land, and asked for again until a late scan's are built.
 * Every few seconds on a stormy day, each ran the loading bar across the top
 * of a map that had nothing to wait for.
 */
export const quietly: Progress = { start() {}, finish() {} };

/** Record what an endpoint just did; the rules are in lib/apiHealth.ts. */
function recordOutcome(id: string, error?: unknown) {
  apiHealth.update((health) => nextHealth(health, id, error));
}

/** What `openapi-fetch` hands back, including the response the status is on. */
interface Answer<T> {
  data?: T;
  error?: unknown;
  response?: Response;
}

/**
 * Whether a call may find nothing there without that being a fault.
 *
 * Some of what the map draws is published on a timer that has nothing to do
 * with this app, and answers 404 until its first capture lands. That is the
 * layer working as designed with nothing yet to show, and it was being counted
 * as a failed API call: the map went degraded, "Something went wrong" came up
 * over it, and both stayed for as long as the upstream had nothing. For the
 * Swiss composite that is every restart of its ingest, and on a backend
 * without it at all, forever.
 *
 * `optional` only accepts the one answer that means absence. Anything else
 * from the same endpoint (a 500, a timeout, a body that will not parse) is a
 * failure and is reported as one, so switching this on cannot hide a route
 * that is broken.
 */
interface RequestOptions {
  optional?: boolean;
}

/** The answer that means "there is nothing here", as opposed to "this broke". */
const isAbsence = (response: Response | undefined): boolean => response?.status === 404;

/**
 * An `optional` endpoint with nothing published yet.
 *
 * Still thrown, because the caller asked for something that is not there and
 * has nothing to draw either way; every one of these call sites already
 * catches. A class rather than a message, so the wrapper's own handler can
 * tell it apart from a failure without reading strings, and so a caller that
 * wants to say "not captured yet" in its own words can too.
 */
export class NothingPublished extends Error {
  readonly endpoint: string;

  constructor(endpoint: string) {
    super(`${endpoint} has nothing published yet`);
    this.name = "NothingPublished";
    this.endpoint = endpoint;
  }
}

/**
 * How long to wait before each retry of a call that failed for the network's
 * sake. Two, so a blip (a dropped connection, one stalled request, a
 * gateway that hiccupped) is ridden out with the loading bar still moving
 * and nothing else to see. Anything longer is an outage, and lib/recovery.ts
 * is what comes back for it.
 */
export const RETRY_DELAYS_MS = [1000, 4000];

/** An HTTP answer that is not the data, kept with its status so it can be judged. */
class Refused extends Error {
  readonly status: number | undefined;

  constructor(id: string, status: number | undefined, error: unknown) {
    super(`${id} failed (${status ?? "no status"}): ${JSON.stringify(error)}`);
    this.name = "Refused";
    this.status = status;
  }
}

const transient = (error: unknown): boolean => (
  error instanceof Refused ? isTransient(error, error.status) : isTransient(error)
);

const offline = (): boolean => typeof navigator !== "undefined" && navigator.onLine === false;

/**
 * Send once, and say whether the backend was there: anything that came back
 * with a response was answered, a 500 included. Only a call that got nothing
 * at all (refused by the network, or stalled) counts against it, and only
 * when its caller has given up on it (`noAnswer`): a retry may yet get through.
 */
async function sendOnce<T>(send: () => Promise<Answer<T>>): Promise<Answer<T>> {
  const answer = await send();
  if (answer.response) reachability.update((r) => answered(r, Date.now()));
  return answer;
}

/** A call sent at `sentAt` has had its last try and got nothing back; see lib/reachability.ts. */
function noAnswer(error: unknown, sentAt: number) {
  if (!(error instanceof Refused) && transient(error)) {
    reachability.update((r) => unanswered(r, sentAt, Date.now()));
  }
}

const pause = (ms: number) => new Promise((resolve) => { setTimeout(resolve, ms); });

/**
 * The same call once more, quietly: no loading bar, no retries, no report.
 * True when it answered, which is recorded as the endpoint recovering.
 */
async function probe<T>(id: string, send: () => Promise<Answer<T>>, optional: boolean): Promise<boolean> {
  const sentAt = Date.now();
  reachability.update(started);
  try {
    const { data, error, response } = await sendOnce(send);
    if (error === undefined && data !== undefined) {
      recordOutcome(id);
      return true;
    }
    if (optional && isAbsence(response)) {
      apiHealth.update((health) => markAbsent(health, id));
      return true;
    }
  } catch (error) {
    // Still down; the next probe asks again.
    noAnswer(error, sentAt);
  } finally {
    reachability.update(finished);
  }
  return false;
}

async function request<T>(
  nanobar: Progress | undefined,
  id: string,
  send: () => Promise<Answer<T>>,
  { optional = false }: RequestOptions = {},
): Promise<T> {
  // The shared bar unless a caller brings its own: every request shows.
  const bar = nanobar ?? progress();
  bar.start(id);
  reachability.update(started);
  try {
    for (let attempt = 0; ; attempt += 1) {
      const sentAt = Date.now();
      try {
        const { data, error, response } = await sendOnce(send);
        if (error === undefined && data !== undefined) {
          recordOutcome(id);
          return data;
        }
        if (optional && isAbsence(response)) {
          apiHealth.update((health) => markAbsent(health, id));
          throw new NothingPublished(id);
        }
        throw new Refused(id, response?.status, error);
      } catch (error) {
        // An absence has already been recorded, and nobody is told the backend is
        // broken over it.
        if (error instanceof NothingPublished) throw error;
        /* Asked again only for what the network did, and not while the browser
           knows it is offline: that ends with an `online` event, and the wake
           it brings refetches everything anyway. */
        const again = RETRY_DELAYS_MS[attempt];
        if (again !== undefined && transient(error) && !offline()) {
          await pause(again);
          continue;
        }
        // Unreachable before failing, so the machine goes straight to offline
        // rather than by way of degraded (lib/connectionState.ts).
        noAnswer(error, sentAt);
        recordOutcome(id, error);
        if (transient(error)) {
          // The reader's network, not a fault of ours: a warning rather than a
          // Sentry report, and asked again until it answers (lib/recovery.ts).
          console.warn(error);
          noteTransientFailure(id, () => probe(id, send, optional));
        } else {
          reportError(error);
        }
        throw error;
      }
    }
  } finally {
    bar.finish(id);
    reachability.update(finished);
  }
}

/**
 * What `/v3/radar/timeseries?products=` may ask for: another observed
 * product's past, or `nowcast`, meteocool's own run for each network around
 * DWD (lib/nowcast.ts).
 */
export type TimeseriesProduct = AlternativeProduct | "nowcast";

/** Radar and nowcast tile metadata for each timestep in the current window. */
export function fetchRadarTimeseries(
  nanobar?: Progress,
  position?: { lat: number; lon: number; network?: CountryNetwork },
  products: readonly TimeseriesProduct[] = [],
) {
  // The chosen product's own past, beside HX's and the networks': asked for,
  // since every frame carries its tile index (lib/observedProduct.ts). And
  // with the experimental setting on, every network's nowcast run.
  const query = { ...position, ...(products.length ? { products: [...products] } : {}) };
  return request(nanobar, "/v3/radar/timeseries", () =>
    apiClient.GET("/v3/radar/timeseries", { params: { query } }));
}

/**
 * The most recent snowfield vector tile set. `optional`: the backend answers
 * 404 until its first snowfield is in, which outside winter is for weeks.
 */
export function fetchSnowOverlay(nanobar?: Progress) {
  return request(
    nanobar,
    "/v3/radar/snow",
    () => apiClient.GET("/v3/radar/snow", {}),
    { optional: true },
  );
}

/**
 * The most recent Swiss reflectivity composite: one frame, not a timeseries.
 *
 * 404s until the first composite lands, same as `fetchPrecipitationTypes`
 * before anything has rendered; callers catch that the same way. `optional`,
 * because that 404 is the ingest's schedule rather than a broken backend and
 * has no business putting the map in its degraded state. Without it, a
 * backend with no Swiss capture at all reported a permanent fault.
 */
export function fetchSwissRadar(nanobar?: Progress) {
  return request(
    nanobar,
    "/v3/radar/switzerland",
    () => apiClient.GET("/v3/radar/switzerland", {}),
    { optional: true },
  );
}

/** The most recent French reflectivity composite; `optional` for the reason Switzerland's is. */
export function fetchFrenchRadar(nanobar?: Progress) {
  return request(
    nanobar,
    "/v3/radar/france",
    () => apiClient.GET("/v3/radar/france", {}),
    { optional: true },
  );
}

/** The most recent Czech reflectivity composite; `optional` for the reason Switzerland's is. */
export function fetchCzechRadar(nanobar?: Progress) {
  return request(
    nanobar,
    "/v3/radar/czechia",
    () => apiClient.GET("/v3/radar/czechia", {}),
    { optional: true },
  );
}

/** The most recent Polish reflectivity composite; `optional` for the reason Switzerland's is. */
export function fetchPolishRadar(nanobar?: Progress) {
  return request(
    nanobar,
    "/v3/radar/poland",
    () => apiClient.GET("/v3/radar/poland", {}),
    { optional: true },
  );
}

/** The most recent Belgian reflectivity composite; `optional` for the reason Switzerland's is. */
export function fetchBelgianRadar(nanobar?: Progress) {
  return request(
    nanobar,
    "/v3/radar/belgium",
    () => apiClient.GET("/v3/radar/belgium", {}),
    { optional: true },
  );
}

/** The most recent Slovak reflectivity composite; `optional` for the reason Switzerland's is. */
export function fetchSlovakRadar(nanobar?: Progress) {
  return request(
    nanobar,
    "/v3/radar/slovakia",
    () => apiClient.GET("/v3/radar/slovakia", {}),
    { optional: true },
  );
}

/** The most recent Lithuanian reflectivity composite; `optional` for the reason Switzerland's is. */
export function fetchLithuanianRadar(nanobar?: Progress) {
  return request(
    nanobar,
    "/v3/radar/lithuania",
    () => apiClient.GET("/v3/radar/lithuania", {}),
    { optional: true },
  );
}

/** The most recent Estonian reflectivity composite; `optional` for the reason Switzerland's is. */
export function fetchEstonianRadar(nanobar?: Progress) {
  return request(
    nanobar,
    "/v3/radar/estonia",
    () => apiClient.GET("/v3/radar/estonia", {}),
    { optional: true },
  );
}

/** The most recent Irish reflectivity composite; `optional` for the reason Switzerland's is. */
export function fetchIrishRadar(nanobar?: Progress) {
  return request(
    nanobar,
    "/v3/radar/ireland",
    () => apiClient.GET("/v3/radar/ireland", {}),
    { optional: true },
  );
}

/** The most recent Croatian reflectivity composite; `optional` for the reason Switzerland's is. */
export function fetchCroatianRadar(nanobar?: Progress) {
  return request(
    nanobar,
    "/v3/radar/croatia",
    () => apiClient.GET("/v3/radar/croatia", {}),
    { optional: true },
  );
}

/**
 * The most recent Danish reflectivity composite; `optional` for the reason
 * Switzerland's is. DMI's is composited every ten minutes, and the timeseries
 * gives each five-minute step the nearest, so two steps show one frame.
 */
export function fetchDanishRadar(nanobar?: Progress) {
  return request(
    nanobar,
    "/v3/radar/denmark",
    () => apiClient.GET("/v3/radar/denmark", {}),
    { optional: true },
  );
}

/** The most recent Dutch reflectivity composite; `optional` for the reason Switzerland's is. */
export function fetchDutchRadar(nanobar?: Progress) {
  return request(
    nanobar,
    "/v3/radar/netherlands",
    () => apiClient.GET("/v3/radar/netherlands", {}),
    { optional: true },
  );
}

/** The most recent Finnish reflectivity composite; `optional` for the reason Switzerland's is. */
export function fetchFinnishRadar(nanobar?: Progress) {
  return request(
    nanobar,
    "/v3/radar/finland",
    () => apiClient.GET("/v3/radar/finland", {}),
    { optional: true },
  );
}

/** The most recent Swedish reflectivity composite; `optional` for the reason Switzerland's is. */
export function fetchSwedishRadar(nanobar?: Progress) {
  return request(
    nanobar,
    "/v3/radar/sweden",
    () => apiClient.GET("/v3/radar/sweden", {}),
    { optional: true },
  );
}

/** The most recent Norwegian reflectivity composite; `optional` for the reason Switzerland's is. */
export function fetchNorwegianRadar(nanobar?: Progress) {
  return request(
    nanobar,
    "/v3/radar/norway",
    () => apiClient.GET("/v3/radar/norway", {}),
    { optional: true },
  );
}

/**
 * The most recent merged European composite (every network's lowest tilts
 * on one grid, a product behind a setting); `optional` for the reason
 * Switzerland's is, and because an environment may not build it at all.
 */
export function fetchEuropeRadar(nanobar?: Progress) {
  return request(
    nanobar,
    "/v3/radar/europe",
    () => apiClient.GET("/v3/radar/europe", {}),
    { optional: true },
  );
}

/**
 * DWD's most recent column maximum, DMAX: the strongest echo over every tilt,
 * on HX's grid, a product the reader can draw instead of HX; `optional` for
 * the reason Switzerland's is, and because a replay renders none.
 */
export function fetchColumnMaximum(nanobar?: Progress) {
  return request(
    nanobar,
    "/v3/radar/dmax",
    () => apiClient.GET("/v3/radar/dmax", {}),
    { optional: true },
  );
}

/**
 * The most recent column maximum of every network (meteocool's, from every
 * tilt of every radar), a product the reader can draw; `optional` for the
 * reason Switzerland's is, and because a replay builds none.
 */
export function fetchEuropeColumnMaximum(nanobar?: Progress) {
  return request(
    nanobar,
    "/v3/radar/colmax",
    () => apiClient.GET("/v3/radar/colmax", {}),
    { optional: true },
  );
}

/** The most recent precipitation-type tile set, absent until one has rendered. */
export function fetchPrecipitationTypes(nanobar?: Progress) {
  return request(
    nanobar,
    "/v3/radar/classification",
    () => apiClient.GET("/v3/radar/classification", {}),
    { optional: true },
  );
}

/**
 * The most recent lightning vector tile set. `optional`: 404 until the first
 * set is built, which on a backend without a strike feed is never.
 */
export function fetchLightningLayer(nanobar?: Progress) {
  return request(
    nanobar,
    "/v3/lightning/layer",
    () => apiClient.GET("/v3/lightning/layer", {}),
    { optional: true },
  );
}

/** Every strike recorded since `baseline`, as a unix timestamp in seconds. */
export function fetchLightningSince(baseline: number, nanobar?: Progress) {
  return request(nanobar, "/v3/lightning/baseline", () =>
    apiClient.GET("/v3/lightning/baseline", { params: { query: { baseline } } }));
}

/**
 * Per-minute strike counts inside `polygon`, for the last half hour.
 *
 * The endpoint takes the polygon as a JSON-encoded string rather than a
 * structured parameter, so the encoding lives here instead of at every call.
 */
export function fetchLightningStats(polygon: number[][], nanobar?: Progress) {
  return request(nanobar, "/v3/lightning/stats", () =>
    apiClient.GET("/v3/lightning/stats", {
      params: { query: { bbox: JSON.stringify({ coordinates: polygon }) } },
    }));
}

/** Recent strikes, so a map that has just loaded can backfill. */
export function fetchLightningCache(nanobar?: Progress) {
  return request(nanobar, "/lightning_cache", () => dataClient.GET("/lightning_cache", {}));
}

/** Every currently active mesocyclone detection. */
export function fetchMesocyclones(nanobar?: Progress) {
  return request(nanobar, "/mesocyclones/all/", () => dataClient.GET("/mesocyclones/all/", {}));
}

/**
 * Cell tracks inside a viewport.
 *
 * Both bounds are sent every time. The window keeps a severe afternoon from
 * returning every storm of the day, and the viewport keeps it from returning
 * every storm in the country. Either alone leaves a response that a phone on
 * a mobile connection would rather not have.
 */
export function fetchCellTracks(
  bbox: [number, number, number, number],
  sinceMinutes: number,
  nanobar?: Progress,
) {
  const since = new Date(Date.now() - sinceMinutes * 60_000).toISOString();
  return request(nanobar, "/cells/tracks", () =>
    dataClient.GET("/cells/tracks", {
      params: { query: { bbox: bbox.map((value) => value.toFixed(3)).join(","), since } },
    }));
}

/** Every cell in the most recent KONRAD3D run, with its forecast centroids. */
export function fetchCurrentCells(nanobar?: Progress) {
  return request(nanobar, "/cells/current", () => dataClient.GET("/cells/current", {}));
}

/**
 * Every storm with a radar volume, from the newest composite scan.
 *
 * Found in the radar composite rather than in KONRAD3D, so most of these are
 * showers `/cells/current` never mentions. They are the clouds a reader can
 * cut open that no warning product would list.
 */
export function fetchCurrentVolumes(nanobar?: Progress, { coarse = false }: { coarse?: boolean } = {}) {
  // Coarse tiles only for the 3D map, which draws them in place of the tiles
  // under them when zoomed out; anything else would draw both.
  return request(nanobar, "/cells/volumes", () => dataClient.GET("/cells/volumes", {
    params: { query: coarse ? { coarse: true } : {} },
  }));
}

/**
 * Every radar in view that has published sweeps lately, or one radar alone,
 * with each tilt's newest sweeps: what site mode draws (lib/siteSweeps.ts).
 *
 * One request for every site in view, as the volumes are; a sweep itself is
 * fetched from the bucket by its `path`. `optional`: a backend from before
 * site sweeps answers 404, which is a map with no radar marks on it.
 */
export function fetchSweeps(
  where: { bbox?: [number, number, number, number]; site?: string },
  nanobar?: Progress,
) {
  const query: { bbox?: string; site?: string } = {};
  if (where.bbox) query.bbox = where.bbox.map((value) => value.toFixed(3)).join(",");
  if (where.site) query.site = where.site;
  return request(nanobar, "/sweeps", () => dataClient.GET("/sweeps", { params: { query } }), { optional: true });
}

/**
 * One cell's complete history, for a detail view.
 *
 * `optional` for a code that came from a link rather than from the map: the
 * backend forgets tracks after a while and answers 404, and a link outliving
 * its storm is not the backend failing.
 */
export function fetchCellTrack(code: string, nanobar?: Progress, options: RequestOptions = {}) {
  return request(nanobar, "/cells/tracks/{code}", () =>
    dataClient.GET("/cells/tracks/{code}", { params: { path: { code } } }), options);
}

/**
 * Storms inside a viewport, seen since `sinceMinutes` ago.
 *
 * Bounded both ways for the reason `fetchCellTracks` is. `optional`: a
 * backend that does not serve storms yet answers 404, which is a map with no
 * storms on it, not a broken backend.
 */
export function fetchStorms(
  bbox: [number, number, number, number],
  sinceMinutes: number,
  nanobar?: Progress,
) {
  const since = new Date(Date.now() - sinceMinutes * 60_000).toISOString();
  return request(nanobar, "/storms", () =>
    dataClient.GET("/storms", {
      params: { query: { bbox: bbox.map((value) => value.toFixed(3)).join(","), since } },
    }), { optional: true });
}

/**
 * One storm with its KONRAD3D tracks and tracker tracks, for
 * the panel. `optional`: a storm past the working set answers 404, which a
 * link outliving its storm is, not the backend failing.
 */
export function fetchStorm(id: string, nanobar?: Progress) {
  return request(nanobar, "/storms/{storm_id}", () =>
    dataClient.GET("/storms/{storm_id}", { params: { path: { storm_id: id } } }), { optional: true });
}
